'use client';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Flex, Link, Text } from '@gjensidige/builders-components';
import {
  Email,
  Facebook,
  Instagram,
  Linkedin,
  Phone,
  PhoneCall,
  Twitter
} from '@gjensidige/builders-icons';
import { FooterNav } from './footer-nav';
import type { FooterProps as Props, OptionalFooterApiUrl } from './types';
import './footer.less';

export const Footer = ({
  footerData,
  className,
  ...rest
}: OptionalFooterApiUrl & JSX.IntrinsicElements['footer']) => {
  const {
    cookiePreferencesLabel,
    copyrightLabel,
    links,
    nav,
    homeUrl,
    social,
    contact
  } = footerData as Props;

  const [currentPath, setCurrentPath] = useState(homeUrl);

  useEffect(() => {
    if (window && window.location && window.location.pathname) {
      setCurrentPath(window.location.pathname);
    }
  }, []);

  return (
    <footer
      className={classNames('builders_global_footer', className)}
      {...rest}
    >
      <Flex center layout="1" align="start">
        <div className="builders_global_topSection">
          {!!(nav?.global || nav?.local?.length) && (
            <FooterNav currentPath={currentPath} nav={nav} />
          )}
          <div className="builders_global_social">
            <Social icon={Phone} social={contact?.phone} protocol="tel:" />
            <Social icon={PhoneCall} social={contact?.callMe} />
            <Social icon={Email} protocol="mailto:" social={contact?.email} />
            <Social social={social?.instagram} icon={Instagram} />
            <Social social={social?.linkedin} icon={Linkedin} />
            <Social social={social?.facebook} icon={Facebook} />
            <Social social={social?.twitter} icon={Twitter} />
          </div>
        </div>

        <div className="builders_global_bottomSection">
          <div className="builders_global_links">
            {!!cookiePreferencesLabel && (
              <a href="#cookie:settings">{cookiePreferencesLabel}</a>
            )}
            {links?.map(({ text, url, newWindow }, index) => (
              <Link
                key={index}
                href={url}
                target={newWindow ? '_blank' : undefined}
              >
                {text || url}
              </Link>
            ))}
          </div>
          {!!copyrightLabel && (
            <div className="builders_global_copyright">
              <Text size="small">{copyrightLabel}</Text>
            </div>
          )}
        </div>
      </Flex>
    </footer>
  );
};

type SocialTypes = {
  social:
    | {
        label: string;
        url: string;
        newWindow?: boolean | undefined;
      }
    | { label: string; target: string }
    | undefined;
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  protocol?: 'tel:' | 'mailto:';
};

const Social = ({ social, icon: Icon, protocol = undefined }: SocialTypes) =>
  !!social && (
    <a
      href={
        protocol && 'target' in social
          ? `${protocol}${social.target}`
          : 'url' in social
          ? social.url
          : undefined
      }
      target={'newWindow' in social && social.newWindow ? '_blank' : undefined}
      rel="noreferrer"
    >
      <Icon />
      {social.label}
    </a>
  );
